import { MaterialReactTable } from 'material-react-table'
import { Button, Flex, Modal, ModalContent, ModalFooter, RadioButton } from 'monday-ui-react-core'
import React, { useEffect, useState } from 'react'
import { buildTableRowId, getRowSelectionBaseKey } from 'utils/commonUtils'
import './TableReport.scss'

const TableReport = ({
    columns,
    tableData,
    enableRowSelection,
    enableManualSorting,
    onPaginationChange,
    pagination,
    sorting,
    onSortingChange,
    rowCount,
    columnVisibility,
    setColumnVisibility,
    onDownloadButtonClicked,
    isLoading,
    isFetching,
    rowSelection,
    setRowSelection,
    rowId,
    isPayout = false,
    actionButtonLabel,
    onActionButtonClicked,
    enableSelectAll = true,
    hasMassDownload = true,
}) => {
    const [showModal, setShowModal] = useState(false)
    const [showCsvError, setShowCsvError] = useState(false)
    const [hideCsvButton, setHideCsvButton] = useState(false)
    const [selectedRadio, setSelectedRadio] = useState('pdf')

    const showMassDownloadUpsell = showModal && !hasMassDownload

    useEffect(() => {
        if (showModal === false) {
            setTimeout(() => {
                setSelectedRadio('pdf')
                setShowCsvError(false)
                setHideCsvButton(false)
            }, 500)
        }
    }, [showModal])

    const handleShow = () => {
        if (!onActionButtonClicked) {
            const shouldHideCsvButton = Object.keys(rowSelection).every((key) => {
                const parts = getRowSelectionBaseKey(key).split(',')
                return parts[parts.length - 1] === 'false'
            })
            setHideCsvButton(shouldHideCsvButton)
            setShowModal(true)
            return
        }

        onActionButtonClicked(rowSelection)
    }

    const handleRadioChange = (event) => {
        setSelectedRadio(event.target.value)
    }

    const closeModal = () => {
        setShowModal(false)
    }

    const onDownloadClicked = (rowSelection, selectedRadio) => {
        if (!hasMassDownload) {
            return
        }
        onDownloadButtonClicked(rowSelection, selectedRadio)
        setShowModal(false)
    }

    return (
        <>
            <MaterialReactTable
                onSortingChange={onSortingChange}
                onPaginationChange={onPaginationChange}
                onColumnVisibilityChange={setColumnVisibility}
                initialState={{
                    columnVisibility: columnVisibility,
                }}
                state={{
                    pagination,
                    sorting,
                    columnVisibility,
                    isLoading,
                    showProgressBars: isFetching,
                    rowSelection,
                    isPayout,
                    selectedRadio,
                }}
                onRowSelectionChange={setRowSelection}
                manualPagination={true}
                manualSorting={enableManualSorting}
                rowCount={rowCount}
                enableExpanding={false}
                enableFilters={false}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableTopToolbar={true}
                enableExpandAll={true}
                enableColumnActions={false}
                enableSelectAll={enableSelectAll}
                positionToolbarAlertBanner="none"
                displayColumnDefOptions={{
                    'mrt-row-expand': { size: 20 },
                    'mrt-row-select': {
                        align: 'center',
                        size: 20,
                    },
                    'mrt-row-actions': {
                        size: 20,
                    },
                }}
                getRowId={(row, index) =>
                    buildTableRowId(rowId, row, `${pagination.pageIndex}-${index}`)
                }
                enableRowSelection={enableRowSelection ?? true}
                renderTopToolbarCustomActions={({ table }) => {
                    const selectedCount = table.getFilteredSelectedRowModel().flatRows.length
                    const hasSelection = selectedCount > 0
                    return (
                        <div className="action-bar">
                            <div className="button-wrapper">
                                <span>{selectedCount} selected</span>
                                <Button
                                    marginLeft={1}
                                    disabled={!hasSelection}
                                    onClick={() => setRowSelection({})}
                                >
                                    Clear Selection
                                </Button>
                                <Button
                                    marginLeft={1}
                                    disabled={!hasSelection}
                                    onClick={handleShow}
                                >
                                    {actionButtonLabel ? actionButtonLabel : 'Download'}
                                </Button>
                            </div>
                        </div>
                    )
                }}
                renderEmptyRowsFallback={() => {
                    return (
                        <p className="text-center mt-4 mb-4">
                            No statements match your search criteria.
                        </p>
                    )
                }}
                muiSelectCheckboxProps={() => ({
                    align: 'center',
                    size: 'small',
                })}
                muiSelectAllCheckboxProps={() => ({
                    align: 'center',
                    size: 'small',
                })}
                muiTablePaginationProps={{
                    rowsPerPageOptions: [10, 25, 50, 100],
                }}
                data={tableData}
                columns={columns}
                selectAllMode="all"
            />
            <Modal
                id="story-book-modal"
                onClose={closeModal}
                title={showMassDownloadUpsell ? 'Bulk Export' : 'Select file type'}
                show={showModal}
            >
                <ModalContent>
                    <div className="monday-storybook-radio-buttons_wrapper-column">
                        {showMassDownloadUpsell && (
                            <div className="mass-download-upgrade-banner" role="status">
                                <p className="mass-download-upgrade-banner__eyebrow">Bulk Export</p>
                                <p className="mass-download-upgrade-banner__title">Export selected statements at once</p>
                                <p className="mass-download-upgrade-banner__text">
                                Generate a single PDF or CSV export of all selected statements.  Cut processing time for high volume analysis.
                                </p>
                                <p className="mass-download-upgrade-banner__cta">
                                Available on premium plans. Email&nbsp;
                                    <a
                                        className="mass-download-upgrade-banner__link"
                                        href="mailto:info@energilitydx.com?subject=Product%20Balancing%20Receiver%20Upgrade%20Inquiry&body=Product%20Balancing%20Bulk%20Export%20-%20Request%20Access%0D%0A%0D%0AHi%2C%20I%27m%20interested%20in%20adding%20Bulk%20Export%20to%20my%20plan.%20Please%20send%20me%20more%20information%20on%20pricing%20and%20next%20steps."
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        info@energilitydx.com
                                    </a>{' '}
                                    to request access.
                                </p>
                            </div>
                        )}
                        {hasMassDownload && (
                            <>
                                <RadioButton
                                    onSelect={handleRadioChange}
                                    checked={selectedRadio === 'pdf'}
                                    value="pdf"
                                    text="PDF"
                                    name="radio-buttons-group-4"
                                />
                                {!hideCsvButton && !isPayout && (
                                    <>
                                        <RadioButton
                                            onSelect={handleRadioChange}
                                            checked={selectedRadio === 'csv'}
                                            value="csv"
                                            text="CSV"
                                            name="radio-buttons-group-4"
                                        />
                                        {showCsvError && (
                                            <p className="csv-error-info">
                                                You do not have download authorization for one or more selected
                                                documents. Those documents will not be added to the CSV.
                                            </p>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </ModalContent>
                <ModalFooter>
                    <Flex gap={12} justify="End">
                        <Button kind="tertiary" onClick={closeModal}>
                            Cancel
                        </Button>
                        {hasMassDownload && (
                            <Button onClick={() => onDownloadClicked(rowSelection, selectedRadio)}>
                                Download
                            </Button>
                        )}
                    </Flex>
                </ModalFooter>
            </Modal>
        </>
    )
}

export default TableReport
